import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import InventoryIcon from "@mui/icons-material/Inventory";
import CloseIcon from "@mui/icons-material/Close";
import { getBoxSizeDisplay } from "../../../utils/boxPricing";


/**
 * Beautiful Order Confirmation Modal
 * Displays order summary before placing the order
 */
const OrderConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  order,
  isLoading,
  specialInstructions,
}) => {
  if (!order) return null;

  const { shippingAddress, totalItem, totalPrice, totalDiscountedPrice, discount } = order;

  return (

    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header with gradient background */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 3,
          px: 3,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Thank you for your order!
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              Order #{order?.id}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ py: 3 }}>
        {/* Delivery Address Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <LocalShippingOutlinedIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="600">
              Delivery Address
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: "grey.50",
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography fontWeight="500">
              {shippingAddress?.firstName} {shippingAddress?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {shippingAddress?.streetAddress}
              {shippingAddress?.city && `, ${shippingAddress?.city}`}
              {shippingAddress?.state && `, ${shippingAddress?.state}`}
              {shippingAddress?.zipCode && ` - ${shippingAddress?.zipCode}`}
            </Typography>
            {shippingAddress?.mobile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Phone: {shippingAddress?.mobile}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Special Instructions Section */}
        {specialInstructions && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <NoteAltOutlinedIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="600">
                Special Instructions
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: "amber.50",
                p: 2,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "amber.200",
              }}
            >
              <Typography variant="body2" color="text.primary" sx={{ fontStyle: "italic" }}>
                "{specialInstructions}"
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* Order Items Display - Task 8 */}
        {order?.orderItems && order.orderItems.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <InventoryIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="600">
                Order Items
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
              {order.orderItems.map((item) => (
                <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, p: 1, bgcolor: "grey.50", borderRadius: 1 }}>
                  <Box
                    component="img"
                    src={item.product?.imageUrl}
                    alt={item.product?.title}
                    sx={{ width: 50, height: 50, borderRadius: 1, objectFit: "cover" }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="500">{item.product?.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.quantity} x {getBoxSizeDisplay(item.size) || "Unit"}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="600">
                    ${item.discountedPrice?.toFixed(2) || item.price?.toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* Order Summary Section */}

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <ReceiptLongOutlinedIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="600">
              Order Summary
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: "grey.50",
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Items ({totalItem} {totalItem === 1 ? "item" : "items"})
              </Typography>
              <Typography variant="body2">${totalPrice?.toFixed(2)}</Typography>
            </Box>
            {discount > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Discount
                </Typography>
                <Typography variant="body2" color="success.main">
                  -${discount?.toFixed(2)}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Delivery
              </Typography>
              <Typography variant="body2" sx={{ color: "primary.main", fontStyle: "italic" }}>
                Calculated at the Time of Delivery
              </Typography>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Total Amount
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                ${totalDiscountedPrice?.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: "info.light",
            borderRadius: 2,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "white" }}>
            We will contact you for payment and shipment details
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: "none",
          }}
          disabled={isLoading}
        >
          Continue Shopping
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1,
            textTransform: "none",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)",
            },
          }}
        >
          View Orders
        </Button>
      </DialogActions>
    </Dialog >
  );
};

export default OrderConfirmationModal;
